/**
 * Schémas Zod du module Campagnes (Lot C) — partagés API ⇄ ERP.
 *
 * Une campagne = UNE année de vendange POUR UN DOMAINE (millésime de récolte).
 * Entité BORNANTE du Lot C : pesées, marcs, bordereaux d'apport et vins clairs
 * primeurs s'y rattachent. Cycle de vie : `ouverte` → `cloturee` (irréversible).
 *
 * Règles métier clés (cf `packages/database/src/schema/campagnes.ts` +
 * `docs/features/lot-c-vinification/01-cadrage-metier.md`) :
 *   - `rendement_max_kg_ha` : paramètre annuel fixé par le Comité Champagne /
 *     INAO, JAMAIS hardcodé. Nullable (peut ne pas être publié à l'ouverture).
 *   - Une SEULE campagne `ouverte` par millésime et par domaine.
 *   - Clôture irréversible : une campagne clôturée alimente la Déclaration de
 *     Récolte (DR). On ne la rouvre pas — correction par opération inverse.
 *
 * Convention :
 *   - `rendement_max_kg_ha` en KG/HA, précision 0,01 (NUMERIC(8,2) côté DB).
 *   - `date_ban` au format ISO `YYYY-MM-DD` (date du ban des vendanges).
 */

import { z } from "zod";

import { idSchema } from "./parcellaire.js";

// ────────────────────────────────────────────────────────────────────────────
// Bornes métier — millésime et rendement
// ────────────────────────────────────────────────────────────────────────────

/** Plus ancien millésime de récolte accepté (garde-fou saisie). */
export const MILLESIME_MIN = 1900;

/**
 * Millésime maximal accepté : année courante + 1 (programmation anticipée de
 * la campagne à venir, ex : préparer la campagne N+1 en fin d'année N).
 */
export function getMillesimeMax(now: Date = new Date()): number {
  return now.getFullYear() + 1;
}

/** Rendement maximal accepté (garde-fou saisie) : 0 exclu, large marge haute. */
export const RENDEMENT_MAX_KG_HA_BORNE = 30_000;

// ────────────────────────────────────────────────────────────────────────────
// Schémas atomiques
// ────────────────────────────────────────────────────────────────────────────

/** Statut du cycle de campagne — aligné sur CHECK SQL de la table `campagnes`. */
export const campagneStatutSchema = z.enum(["ouverte", "cloturee"]);
export type CampagneStatut = z.infer<typeof campagneStatutSchema>;

/** Réexport pour confort d'import côté front (validation des IDs préfixés). */
export { idSchema };

export const millesimeSchema = z
  .number({
    invalid_type_error: "Millésime invalide.",
    required_error: "Millésime requis.",
  })
  .int("Le millésime doit être une année entière.")
  .min(MILLESIME_MIN, `Millésime trop ancien (≥ ${MILLESIME_MIN}).`)
  .max(getMillesimeMax(), `Millésime trop dans le futur (≤ ${getMillesimeMax()}).`);

/**
 * Rendement maximum AOC commercialisable (kg/ha). Paramètre annuel — jamais
 * hardcodé. Optionnel à la création (peut ne pas être encore publié au moment
 * d'ouvrir la campagne). Strictement positif quand fourni.
 */
export const rendementMaxKgHaSchema = z
  .number({ invalid_type_error: "Rendement invalide." })
  .positive("Le rendement doit être supérieur à 0.")
  .max(RENDEMENT_MAX_KG_HA_BORNE, `Rendement maximum : ${RENDEMENT_MAX_KG_HA_BORNE} kg/ha.`);

/** Date du ban des vendanges au format ISO `YYYY-MM-DD`. */
export const dateBanSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date du ban invalide (format attendu AAAA-MM-JJ).");

// ────────────────────────────────────────────────────────────────────────────
// Inputs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Input `campagne.creer` — ouvre une nouvelle campagne (statut `ouverte`).
 *
 * Le couple (domaine, millésime) est unique : tenter d'ouvrir une 2ᵉ campagne
 * sur un millésime déjà présent est refusé côté use case (erreur métier dédiée).
 */
export const campagneCreerInput = z.object({
  millesime: millesimeSchema,

  /** Paramètre annuel CIVC/INAO — optionnel à l'ouverture, jamais hardcodé. */
  rendement_max_kg_ha: rendementMaxKgHaSchema.optional(),

  /** Date du ban des vendanges (informative, optionnelle). */
  date_ban: dateBanSchema.optional().or(z.literal("").transform(() => undefined)),

  /** Notes libres (conditions climatiques, décisions interpro, etc.). */
  notes: z
    .string()
    .trim()
    .max(4000, "Notes trop longues (4000 caractères max).")
    .optional()
    .or(z.literal("").transform(() => undefined)),
});
export type CampagneCreerInput = z.infer<typeof campagneCreerInput>;

/** Input `campagne.getById` — fiche détail d'une campagne. */
export const campagneGetByIdInput = z.object({
  id: idSchema,
});
export type CampagneGetByIdInput = z.infer<typeof campagneGetByIdInput>;

/**
 * Input `campagne.lister` — filtres optionnels pour la liste tenant.
 * Volume très faible (1 campagne/an, quelques dizaines au total) → pas de
 * pagination.
 */
export const campagneListerInput = z
  .object({
    statut: campagneStatutSchema.optional(),
  })
  .optional();
export type CampagneListerInput = z.infer<typeof campagneListerInput>;

/**
 * Input `campagne.cloturer` — clôture définitive d'une campagne.
 *
 * Action CRITIQUE et IRRÉVERSIBLE : fige la campagne (alimente la DR). Aucun
 * paramètre de réouverture n'existe — une correction passe par une opération
 * inverse, jamais par un UPDATE destructif du statut.
 */
export const campagneCloturerInput = z.object({
  id: idSchema,
});
export type CampagneCloturerInput = z.infer<typeof campagneCloturerInput>;

// ────────────────────────────────────────────────────────────────────────────
// Output — validation de SORTIE (cf règle « Zod entrée ET sortie »)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Forme de sortie d'une campagne renvoyée par l'API. Les NUMERIC Postgres
 * arrivent en `string` via Drizzle ; on conserve la string pour ne pas perdre
 * de précision côté transport (formatage côté UI).
 */
export const campagneOutput = z.object({
  id: idSchema,
  millesime: z.number().int(),
  statut: campagneStatutSchema,
  rendement_max_kg_ha: z.string().nullable(),
  plafond_pressurage_l_160kg: z.string().nullable(),
  source_parametres_pressurage: z.string().nullable(),
  date_decision_parametres_pressurage: z.string().nullable(),
  qualification_pressurage: z.enum(["a_qualifier", "qualifiee"]),
  surface_reference_rqi_ha: z.string().nullable(),
  plafond_rqi_kg_ha: z.string().nullable(),
  qualification_rqi: z.enum(["a_qualifier", "qualifiee"]),
  date_ban: z.string().nullable(),
  notes: z.string().nullable(),
  cree_le: z.date(),
  modifie_le: z.date(),
});
export type CampagneOutput = z.infer<typeof campagneOutput>;
